import React, { useEffect, useRef, useState } from "react";

/**
 * The map's first appearance, in three beats: the states, then the pins
 * landing one after another, then the lines drawn between them. Only on a page
 * load or refresh: a chip, tenant or route change glides instead (SitesMap),
 * since making the reader wait through the intro again is friction.
 */

const PINS_AT = 300;
const PIN_STAGGER = 90;
const PIN_MS = 560;
const DRAW_MS = 750;
const FADE_MS = 380;

/**
 * Module state on purpose: it lives exactly as long as the page. Coming back
 * to the Overview, or another tenant remounting the map, does not replay it.
 */
let hasIntroPlayed = false;

/** The page's first topology was the diagram: the map shown later is not a first load. */
export function skipTopologyIntro(): void {
  hasIntroPlayed = true;
}

export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface IntroTimeline {
  /** When the pin `index`-th from the left lands. */
  pinAt: (index: number) => number;
  linesAt: number;
  cloudsAt: number;
  labelsAt: number;
  endsAt: number;
}

export function introTimeline(pinCount: number): IntroTimeline {
  // Lines start as the last pin settles, not after its bounce: the beats overlap.
  const linesAt = PINS_AT + Math.max(pinCount - 1, 0) * PIN_STAGGER + PIN_MS * 0.6;
  return {
    pinAt: (index) => PINS_AT + index * PIN_STAGGER,
    linesAt,
    cloudsAt: linesAt + DRAW_MS * 0.5,
    labelsAt: linesAt + DRAW_MS,
    endsAt: linesAt + DRAW_MS + FADE_MS,
  };
}

export type Entrance = "pop" | "fade" | "draw";

const ENTRANCE_CLASS: Record<Entrance, string> = {
  pop: "motion-safe:animate-topology-pop",
  fade: "motion-safe:animate-topology-in",
  draw: "motion-safe:animate-topology-draw",
};

export interface EntranceProps {
  className: string;
  style?: React.CSSProperties;
  /** Drawn during the intro: a line then draws itself instead of fading in. */
  isIntro: boolean;
}

/**
 * `entrance(key, kind, delay)`: how an element comes in. During the intro, its
 * beat of the intro. After it, an element still on screen from the intro drops
 * its animation without a flicker (it has finished), and anything mounted from
 * then on — new, or back after a chip change — only fades in. Never the intro's
 * delays again: that is what made a chip change wait for the DCI caption.
 */
export function useTopologyIntro(isReady: boolean, endsAt: number) {
  const [isOver, setIsOver] = useState(hasIntroPlayed);
  // Decided at mount, so StrictMode's second effect run keeps the intro it started.
  const ownsIntro = useRef(!hasIntroPlayed);
  const delays = useRef(new Map<string, number>());
  /** Keys on screen at the last commit, to tell an element that stays from one that mounts. */
  const shown = useRef(new Set<string>());
  const showing = new Set<string>();
  useEffect(() => {
    shown.current = showing;
  });
  const endsAtRef = useRef(endsAt);
  endsAtRef.current = endsAt;

  useEffect(() => {
    if (!isReady || isOver) return;
    if (!ownsIntro.current || prefersReducedMotion()) {
      setIsOver(true);
      return;
    }
    hasIntroPlayed = true;
    const timer = window.setTimeout(() => setIsOver(true), endsAtRef.current);
    return () => window.clearTimeout(timer);
  }, [isReady, isOver]);

  const entrance = (key: string, kind: Entrance, delay: number): EntranceProps => {
    showing.add(key);
    if (!isOver) {
      const introAt = delays.current.get(key) ?? delay;
      delays.current.set(key, introAt);
      return { className: ENTRANCE_CLASS[kind], style: { animationDelay: `${introAt}ms` }, isIntro: true };
    }
    if (delays.current.has(key)) {
      if (shown.current.has(key)) return { className: "", isIntro: false };
      // Mounted again: from here on it is any other element, or its fade would be cut short.
      delays.current.delete(key);
    }
    return { className: ENTRANCE_CLASS.fade, isIntro: false };
  };

  return { isOver, entrance };
}
